// Recent-duplicate detection for the incident form.
//
// Several admins can be watching the same scanner traffic, and the same job
// gets posted twice within a few minutes often enough to be worth catching.
// This is a warning, never a block: a second alarm at an address that already
// has an incident is a real, correct second incident, and the admin is the one
// who can tell the difference.
//
// The check reads the existing list endpoint rather than adding a server-side
// one. That endpoint's date filter snaps to whole days, so the window is
// applied here: ask for the most recent incidents in this locality and keep the
// ones inside the last half hour. Sorted newest-first with a 100-row ceiling,
// that covers thirty minutes with a lot of room to spare.
import { api } from "includes";

const WINDOW_MS = 30 * 60 * 1000;
const SCAN_LIMIT = 100;

export type DuplicateHit = {
  _id: string;
  title: string;
  address: string;
  box: string;
  createdAt: string;
  minutesAgo: number;
  reason: "box" | "address";
};

/** Addresses are typed by hand and arrive from Google's geocoder in more than
 *  one shape, so compare on something looser than an exact string: case,
 *  punctuation and repeated spaces all stop mattering. Deliberately not
 *  expanding "St"/"Street" — over-matching turns a useful warning into noise
 *  the admin learns to click through. */
const normalizeAddress = (value: any) =>
  String(value || "")
    .toLowerCase()
    .replace(/[.,#]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeBox = (value: any) => String(value || "").trim().toLowerCase();

export function findRecentDuplicates(
  opts: {
    localityId: string;
    address: any;
    box: any;
    excludeId?: string | null;
    auth?: string;
  },
  cb: (hits: DuplicateHit[]) => void
) {
  const address = normalizeAddress(opts.address);
  const box = normalizeBox(opts.box);

  // Nothing to compare against yet.
  if (!opts.localityId || (!address && !box)) {
    cb([]);
    return;
  }

  const query =
    `?sortBy=createdAt&sortDir=desc&offset=1&limit=${SCAN_LIMIT}` +
    `&query=${JSON.stringify({ locality: [opts.localityId] })}`;

  api.call(
    { url: "admin/incident", method: "GET", auth: opts.auth || "token", query },
    (res: any) => {
      if (res.status !== 200) {
        // A failed lookup must not stand between the admin and posting an
        // incident — this is an advisory, so treat "unknown" as "no warning".
        cb([]);
        return;
      }

      const now = Date.now();
      const hits: DuplicateHit[] = [];

      (res.data?.data || []).forEach((row: any) => {
        if (!row?.createdAt) return;
        if (opts.excludeId && row._id === opts.excludeId) return;

        const age = now - new Date(row.createdAt).getTime();
        if (age < 0 || age > WINDOW_MS) return;

        const rowBox = normalizeBox(row.field3Value);
        const rowAddress = normalizeAddress(row.address);

        // Box first: it is the more precise claim of "same job", so when both
        // match it is the one worth naming.
        let reason: "box" | "address" | null = null;
        if (box && rowBox && rowBox === box) reason = "box";
        else if (address && rowAddress && rowAddress === address) reason = "address";
        if (!reason) return;

        hits.push({
          _id: row._id,
          title: row.field1Value || "Untitled incident",
          address: row.address || "",
          box: row.field3Value || "",
          createdAt: row.createdAt,
          minutesAgo: Math.max(0, Math.round(age / 60000)),
          reason,
        });
      });

      cb(hits);
    }
  );
}
